import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { EvalDashboard } from './eval-dashboard.js';
import { useEvalDashboardData, useFailedEvals, useRescoreReview, useReviewDetail } from './use-eval-dashboard-data.js';

import type { EvalKindOption } from './components/eval-kind-toggle.js';
import type { EvalDashboardTab } from './eval-dashboard.js';

const searchSchema = z.object({
	fixtures: z.coerce.boolean().optional(),
	tab: z.enum(['cohort', 'overview', 'needs-attention']).catch('cohort').default('cohort'),
	kind: z.enum(['training', 'production']).catch('training').default('training'),
});

export const Route = createFileRoute('/_authorization-gate/wizard/admin/eval/')({
	validateSearch: searchSchema,
	component: WizardAdminEvalPage,
	head: () => ({ meta: [{ title: 'Operator Eval • Wizard Admin • Cadstrom' }] }),
});

function WizardAdminEvalPage() {
	const search = useSearch({ from: '/_authorization-gate/wizard/admin/eval/' });
	const fixturesMode = Boolean(search.fixtures);
	const activeTab = search.tab;
	const activeKind = search.kind;
	const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);
	const { plans, operators, heatmapRows } = useEvalDashboardData({
		fixtures: fixturesMode,
		operatorIds: selectedOperatorIds.length > 0 ? selectedOperatorIds : undefined,
		evalKind: activeKind,
	});
	const failedEvals = useFailedEvals(fixturesMode, activeKind);
	const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
	const selectedDetail = useReviewDetail({ reviewId: selectedReviewId, fixtures: fixturesMode });
	const rescoreMutation = useRescoreReview();
	const navigate = useNavigate();

	function handleRowClick(reviewId: string) {
		setSelectedReviewId(reviewId.length > 0 ? reviewId : null);
	}

	function handleOperatorChange(operatorIds: string[]) {
		setSelectedOperatorIds(operatorIds);
	}

	function handleOperatorClick(userId: string) {
		void navigate({ to: '/wizard/admin/eval/operator/$userId', params: { userId } });
	}

	function handleRescore(reviewId: string) {
		// Fixture review ids don't exist on the backend; stay offline.
		if (fixturesMode) {
			return;
		}
		void rescoreMutation.mutateAsync({ reviewId });
	}

	function handleTabChange(tab: EvalDashboardTab) {
		void navigate({
			to: '/wizard/admin/eval',
			search: (prev) => ({ ...prev, tab: tab === 'cohort' ? undefined : tab }),
			replace: true,
		});
	}

	function handleKindChange(kind: EvalKindOption) {
		// The open detail belongs to the previous population's row list, so a
		// kind switch closes it rather than stranding a row that is no longer shown.
		setSelectedReviewId(null);
		void navigate({
			to: '/wizard/admin/eval',
			search: (prev) => ({ ...prev, kind: kind === 'training' ? undefined : kind }),
			replace: true,
		});
	}

	return (
		<EvalDashboard
			plans={plans}
			heatmapRows={heatmapRows}
			operators={operators}
			failedEvals={failedEvals}
			operatorIds={selectedOperatorIds}
			onRowClick={handleRowClick}
			onOperatorChange={handleOperatorChange}
			onOperatorClick={handleOperatorClick}
			selectedDetail={selectedDetail}
			onRescore={handleRescore}
			rescoring={rescoreMutation.isPending}
			tab={activeTab}
			onTabChange={handleTabChange}
			evalKind={activeKind}
			onEvalKindChange={handleKindChange}
		/>
	);
}
